import { Box, ScrollView, Text } from "native-base";
import { useQuery } from "react-query";
import { API } from "../api/types";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Colors from "../const/Colors";
import LoadingView from "./element/LoadingView";
import PopupMessage from "./element/PopupMessage";
import ApiServices from "../api/api";
import { toMessageError } from "../util/RequestUtil";
import { ppw } from "../util/LogUtil";
import { useWindowDimensions } from "react-native";
import { SceneMap, TabBar, TabView } from "react-native-tab-view";
import ResultChart from "./element/ResultChart";
import BarDate from "./element/BarDate";
import { useAppDispatch, useAppSelector } from "../store/store";
import { actResult, slResult } from "../store/features/resultSlice";
import { getInitRoute } from "../util/CookieUtil";
import { cloneDeep } from "lodash";
import AdvicesView from "./element/AdvicesView";

const getBarDateItemsDefault = (): API.BarDateItem[] => {
  const defaultItemsCount = 6;
  let items: API.BarDateItem[] = [];

  for (let i = 0; i < defaultItemsCount; i++) {
    items.push({
      text: (i + 1).toString(),
      isPicked: i === 0,
      index: i,
    });
  }

  return items;
};

const getBarDateItemPickedDefault = (
  defaultIndex: number = 0
): API.BarDateItem => {
  return {
    text: (defaultIndex + 1).toString(),
    isPicked: true,
    index: defaultIndex,
  };
};

const getContentInfoWindow = (
  barDateItemPicked: API.BarDateItem,
  params: API.ResultTab | null
): string => {
  try {
    let indexSelected = barDateItemPicked.index;

    let v = params?.data?.[indexSelected];

    if (v === null || v === undefined) {
      return "--";
    } else {
      return `${v}%`;
    }
  } catch (e) {
    return "--";
  }
};

const ResultTab = ({ route }: { route: any }) => {
  //=========== FIELD ==========
  const params: API.ResultTab | null = route;

  ppw("ResultTab().params", params);

  //=========== HOOK ==========
  const { t } = useTranslation();
  const { tabSelectedIndex } = useAppSelector(slResult);

  const [chartData, setChartData] = useState<API.ResultChart[]>([]);
  const [barDateItems, setBarDateItems] = useState<API.BarDateItem[]>(
    getBarDateItemsDefault()
  );
  const [barDateItemPicked, setBarDateItemPicked] = useState<API.BarDateItem>(
    getBarDateItemPickedDefault()
  );
  const [contentPicked, setContentPicked] = useState<string>("--");

  //=========== CHART DATA ==========
  const toChartData = (data?: number[]): API.ResultChart[] => {
    let chartData =
      data?.map((m, i) => {
        return {
          x: i + 2,
          y: m,
        };
      }) ?? [];

    // ppw("toChartData().chartData", chartData);
    // ppw("toChartData().params", params);
    return chartData;
  };

  //=========== LISTENER ==========
  const onPressBarDateItem = (item: API.BarDateItem) => {
    setBarDateItemPicked(item);
  };

  const onChangedParams = () => {
    let d = toChartData(params?.data);
    setChartData(d);
    setContentPicked(getContentInfoWindow(barDateItemPicked, params));
  };

  const onChangedTab = () => {
    setBarDateItemPicked(getBarDateItemPickedDefault());
  };

  const onChangedBarDate = () => {
    let newBarDateItems = cloneDeep(barDateItems);
    newBarDateItems.forEach((m) => {
      m.isPicked = barDateItemPicked.index === m.index;
    });
    setBarDateItems(newBarDateItems);
    setContentPicked(getContentInfoWindow(barDateItemPicked, params));
  };

  //=========== HOOK - VIEW ==========
  useEffect(() => {
    onChangedParams();
  }, [params]);

  useEffect(() => {
    onChangedTab();
  }, [tabSelectedIndex]);

  useEffect(() => {
    onChangedBarDate();
  }, [barDateItemPicked]);

  //=========== OUT ==========
  return (
    <ScrollView display={"flex"} bg={Colors.white} flex={1} width={"100%"}>
      <ResultChart data={chartData} />
      <BarDate item={barDateItems} onPressItem={onPressBarDateItem} />
      <Text
        display={"flex"}
        w={"100vw"}
        fontSize={16}
        justifyContent={"center"}
        alignItems={"center"}
        py={4}
        color={Colors.black}
        paddingX={"24px"}
      >
        {t("argYearLater", { arg: barDateItemPicked.text ?? "" })}
      </Text>
      <Text
        id={"probabilityT"}
        display={"flex"}
        w={"100vw"}
        fontSize={22}
        justifyContent={"center"}
        alignItems={"center"}
        paddingX={"24px"}
        textAlign={"center"}
        color={Colors.black}
      >
        {params?.probability ?? ""}
      </Text>
      <Text
        display={"flex"}
        w={"100vw"}
        fontSize={24}
        justifyContent={"center"}
        alignItems={"center"}
        py={2}
        color={Colors.black}
        paddingX={"24px"}
      >
        {contentPicked}
      </Text>
      <Text
        id={"diseaseT"}
        display={"flex"}
        w={"100vw"}
        fontSize={16}
        justifyContent={"center"}
        alignItems={"center"}
        paddingX={"24px"}
        py={2}
        textAlign={"center"}
        color={Colors.black}
      >
        {params?.disease ?? ""}
      </Text>
      <Text
        id={"noteT"}
        display={"flex"}
        w={"100vw"}
        fontSize={16}
        justifyContent={"center"}
        alignItems={"center"}
        paddingX={"24px"}
        py={2}
        textAlign={"center"}
        color={Colors.black}
      >
        {t("base_on_standard")}
      </Text>
      <Box
        id={"legendB"}
        display={"flex"}
        w={"100vw"}
        // background={Colors.redError}
        paddingX={"24px"}
        paddingY={"8px"}
      >
        <Box
          id={"legendBorderB"}
          display={"flex"}
          w={"100%"}
          borderColor={Colors.resultLegendBorder}
          borderWidth={"2px"}
          // background={Colors.chartGray}
        >
          <Text
            id={"legendT"}
            display={"flex"}
            w={"100%"}
            padding={"8px"}
            fontSize={16}
            justifyContent={"center"}
            alignItems={"center"}
            textAlign={"center"}
            color={Colors.black}
          >
            {params?.legend?.replaceAll("<br>", "\n") ?? ""}
          </Text>
        </Box>
      </Box>
      <Box w={"100vw"} h={"8px"} />
      <AdvicesView type={params?.key} />
      <Box w={"100vw"} h={"16px"} />
    </ScrollView>
  );
};

const renderScene = SceneMap({
  ldl: ResultTab,
  bmi: ResultTab,
  scr: ResultTab,
  tg: ResultTab,
  hdl: ResultTab,
  sbp: ResultTab,
  dbp: ResultTab,
  gpt: ResultTab,
  got: ResultTab,
  rgtp: ResultTab,
  hba1c: ResultTab,
});

const prepareId = (route: any) => {
  const ckId = getInitRoute().id;
  if (ckId != undefined) return ckId;

  return route?.params?.id ?? 0;
};

const Result = ({ route }: { route: any }) => {
  //=========== HOOK ==========
  const { t } = useTranslation();
  const layout = useWindowDimensions();
  const d = useAppDispatch();

  const id = prepareId(route);
  ppw("Result().id", id);

  const [baseError, setBaseError] = useState<string | undefined>(undefined);

  const prepareResultTabData = (res: API.ResultResponse | null) => {
    return [
      {
        key: "ldl",
        title: t("long_ldl"),
        data: res?.body.prediction.ldl,
        probability: t("probability_ldl"),
        disease: t("disease_ldl"),
        legend: t("legend_ldl"),
        advice: res?.body?.advice?.ldl,
      },
      {
        key: "bmi",
        title: t("long_bmi"),
        data: res?.body.prediction.bmi,
        probability: t("probability_bmi"),
        disease: t("disease_bmi"),
        legend: t("legend_bmi"),
      },
      {
        key: "scr",
        title: t("long_scr"),
        data: res?.body.prediction.scr,
        probability: t("probability_scr"),
        disease: t("disease_scr"),
        legend: t("legend_scr"),
        advice: res?.body?.advice?.scr,
      },
      {
        key: "tg",
        title: t("long_tg"),
        data: res?.body.prediction.tg,
        probability: t("probability_tg"),
        disease: t("disease_tg"),
        legend: t("legend_tg"),
      },
      {
        key: "hdl",
        title: t("long_hdl"),
        data: res?.body.prediction.hdl,
        probability: t("probability_hdl"),
        disease: t("disease_hdl"),
        legend: t("legend_hdl"),
        advice: res?.body?.advice?.hdl,
      },
      {
        key: "sbp",
        title: t("long_sbp"),
        data: res?.body.prediction.sbp,
        probability: t("probability_sbp"),
        disease: t("disease_sbp"),
        legend: t("legend_sbp"),
        advice: res?.body?.advice?.sbp,
      },
      {
        key: "dbp",
        title: t("long_dbp"),
        data: res?.body.prediction.dbp,
        probability: t("probability_dbp"),
        disease: t("disease_dbp"),
        legend: t("legend_dbp"),
      },
      {
        key: "gpt",
        title: t("long_gpt"),
        data: res?.body.prediction.gpt,
        probability: t("probability_gpt"),
        disease: t("disease_gpt"),
        legend: t("legend_gpt"),
      },
      {
        key: "got",
        title: t("long_got"),
        data: res?.body.prediction.got,
        probability: t("probability_got"),
        disease: t("disease_got"),
        legend: t("legend_got"),
        advice: res?.body?.advice?.got,
      },
      {
        key: "rgtp",
        title: t("long_rgtp"),
        data: res?.body.prediction.rgtp,
        probability: t("probability_rgtp"),
        disease: t("disease_rgtp"),
        legend: t("legend_rgtp"),
      },
      {
        key: "hba1c",
        title: t("long_hba1c"),
        data: res?.body.prediction.hba1c,
        probability: t("probability_hba1c"),
        disease: t("disease_hba1c"),
        legend: t("legend_hba1c"),
        advice: res?.body?.advice?.hba1c,
      },
    ];
  };

  const [index, setIndex] = React.useState(0);
  const [routes, setRoutes] = React.useState<API.ResultTab[]>(
    prepareResultTabData(null)
  );

  //=========== API - READ ADVICES ==========
  const callApiReadAdvices = () => {
    const req: API.ReadAdvicesRequest = {
      id: id,
    };
    d(actResult.setPredictId(id));
    d(actResult.readAdvices(req));
  };

  //=========== API - DETAIL ==========
  const { refetch: callApiGetResult, isLoading } = useQuery<
    API.ResultResponse | null,
    Error
  >({
    queryKey: ["get-result"],
    cacheTime: 0,
    retry: 0,
    enabled: false, // NOT auto call
    queryFn: async () => {
      if (!id) return await null;
      return await ApiServices.getResult({ id: id });
    },
    onSuccess: (res: any) => {
      const tabData = prepareResultTabData(res);
      setRoutes(tabData);
    },
    onError: (err: any) => {
      let message = toMessageError(err);
      ppw("getResult().onError().message", message);
      setBaseError(message);
    },
  });

  useEffect(() => {
    callApiGetResult();
    callApiReadAdvices();
  }, []);

  //=========== LISTENER ==========
  let onOkPopupMessageError = () => {
    setBaseError(undefined);
  };

  const onTabChange = (index: number) => {
    setIndex(index);
    d(actResult.setTabSelectedIndex(index));
  };

  //=========== OUT ==========
  return (
    <Box display={"flex"} flex={1} backgroundColor={"white"}>
      {baseError != null && (
        <PopupMessage
          isOpen={true}
          message={baseError}
          onOk={onOkPopupMessageError}
        />
      )}
      {isLoading && <LoadingView isOpen={true} />}
      <TabView
        navigationState={{ index, routes }}
        renderScene={renderScene}
        onIndexChange={onTabChange}
        initialLayout={{ width: layout.width }}
        swipeEnabled={false}
        animationEnabled={false}
        renderTabBar={(props) => {
          return (
            <TabBar
              {...props}
              indicatorStyle={{ backgroundColor: Colors.resultChartDot }}
              style={{ backgroundColor: Colors.white, elevation: 0 }}
              scrollEnabled={true}
              tabStyle={{
                width: 200,
              }}
              renderLabel={({ route, focused }) => {
                return (
                  <Text
                    display={"flex"}
                    justifyContent={"center"}
                    alignItems={"center"}
                    width={"200px"}
                    numberOfLines={1}
                    ellipsizeMode="tail"
                    overflow={"hidden"}
                    paddingX={"8px"}
                    fontSize={16}
                    color={Colors.black}
                  >
                    {route?.title ?? ""}
                  </Text>
                );
              }}
            />
          );
        }}
      />
    </Box>
  );
};

export default Result;
